import {hasValue} from '@famly/stat_ts-utils_has-value';
import {getFromLocalStorage} from '@famly/stat_ts-utils_local-storage';

export type Crew = 'enr' | 'stat' | 'fin' | 'inv' | 'pay' | 'gro' | 'aim' | 'tail' | 'go' | 'nux' | 'crm' | 'ai';

export type FeatureFlag = {
    flag: string;
    title: string;
    description?: string;
    crew: Crew;
};

export type ToolingFlag = {
    flag: string;
    title: string;
    description?: string;
};

export type DebuggerFlags = {
    [flagId: string]: FeatureFlag | ToolingFlag;
};

export const DebugFeatureLocalStorageFlags = {
    SHOW_DEFAULT_FTE: {
        flag: 'SHOW_DEFAULT_FTE',
        title: 'Show default FTE',
        description: 'Toggle this to show default fte.',
        crew: 'stat',
    },
    UNIQUE_PARENT_EXPERIENCE: {
        flag: 'UNIQUE_PARENT_EXPERIENCE',
        title: 'Unique parent experience home and explore',
        description: 'Toggle this to show unique parent experience home and explore.',
        crew: 'enr',
    },
    SHOW_FINANCE_DEBUGGING_BUTTON: {
        flag: 'SHOW_FINANCE_DEBUGGING_BUTTON',
        title: 'Show finance debugging button',
        description: 'Show finance debugging button to access Finance debugging page.',
        crew: 'fin',
    },
    SHOW_BULK_PLAN_DELETE: {
        flag: 'SHOW_BULK_PLAN_DELETE',
        title: 'Show Bulk Plan delete option',
        description: 'Toggle this to show the bulk delete latest plan in plans page',
        crew: 'inv',
    },
    OVERRIDE_STRETCHED_HOURS: {
        flag: 'OVERRIDE_STRETCHED_HOURS',
        title: 'Override stretched hours',
        description:
            'Toggle this to show the stretched hours section in the funding settings. The site also needs the UK child entitlements feature.',
        crew: 'inv',
    },
    SHOW_BULK_BILLING_PROFILE_UPDATE: {
        flag: 'SHOW_BULK_BILLING_PROFILE_UPDATE',
        title: 'Show Bulk Billing Profile Update in the Plans page',
        description: 'Used to handle bulk billing profile update in the Plans page',
        crew: 'inv',
    },
    ENABLE_MIGRATE_V1_TO_V2: {
        flag: 'ENABLE_MIGRATE_V1_TO_V2',
        title: 'Enable V1 to V2 billing profile migration',
        description:
            'Toggle this on to enable the bulk migration flow from V1 billing profiles (ACTUAL_SESSIONS, ANNUALIZED, ANNUALIZED_MID_MONTH) to their V2 equivalents (ACTUALS_V2, ANNUALIZED_V2).',
        crew: 'inv',
    },
    ELIGIBILITY_CODE_VALIDITY_DATES: {
        flag: 'ELIGIBILITY_CODE_VALIDITY_DATES',
        title: 'Eligibility code validity dates',
        description:
            'Toggle this on to show the start, end and grace period end dates of the eligibility code in the Manage and View eligibility code modals. They stay hidden until the eligibility code status they feed is built.',
        crew: 'inv',
    },
    NEW_BATCH_INVOICE_PAGE: {
        flag: 'NEW_BATCH_INVOICE_PAGE',
        title: 'New batch invoicing page',
        description:
            'Toggle this on to replace the New invoices mode of the batch invoicing page with the rebuilt one.',
        crew: 'inv',
    },
    NEW_FORM_SUBMIT_PAGE_LAYOUT: {
        flag: 'NEW_FORM_SUBMIT_PAGE_LAYOUT',
        title: 'New form submit page layout',
        description: 'Turn this on to enable the new form submit page layout.',
        crew: 'aim',
    },
    FOLLOWUP_FORM_FEES: {
        flag: 'FOLLOWUP_FORM_FEES',
        title: 'Follow-up form fees',
        description: 'Turn this on to enable payments in follow-up forms',
        crew: 'gro',
    },
    MOBILE_APP_PACKAGE_PURCHASE_MAGIC_LINK_REDIRECT: {
        flag: 'MOBILE_APP_PACKAGE_PURCHASE_MAGIC_LINK_REDIRECT',
        title: 'Mobile app package purchase magic link redirect',
        crew: 'gro',
    },
    MEAL_PLANS_DND: {
        flag: 'MEAL_PLANS_DND',
        title: 'Enable dnd of food items in meal plans',
        crew: 'aim',
    },
    BULK_ADD_ATTENDANCE: {
        flag: 'BULK_ADD_ATTENDANCE',
        title: 'Show the bulk add attendance modal',
        crew: 'stat',
    },
    SMS_NOTIFICATIONS: {
        flag: 'SMS_NOTIFICATIONS',
        title: 'Enable SMS notifications in Newsfeed and Messages',
        crew: 'aim',
    },
    SHOW_PURCHASES_NEW_DESIGN_ON_MOBILE: {
        flag: 'SHOW_PURCHASES_NEW_DESIGN_ON_MOBILE',
        title: 'Show new design for purchases on mobile and automatic pricing',
        crew: 'gro',
    },
    MF_LISTS_MANAGE_CHILDREN: {
        flag: 'MF_LISTS_MANAGE_CHILDREN',
        title: 'Enable manage children MF List implementation',
        crew: 'aim',
    },
    NEW_REPORT_CREATE_FLOW: {
        flag: 'NEW_REPORT_CREATE_FLOW',
        title: 'Enable new "Create report" flow',
        description: 'Enables the new "Create report" flow on reports pages.',
        crew: 'aim',
    },
    LIMITED_CONTACT_ROLE: {
        flag: 'LIMITED_CONTACT_ROLE',
        title: 'Enable Limited Contact Role toggle',
        description: 'Shows the Limited Contact Role toggle in the child profile contacts form.',
        crew: 'aim',
    },
    BILL_PAYER_ENABLE_LEGACY_CSV_REPORT: {
        flag: 'BILL_PAYER_ENABLE_LEGACY_CSV_REPORT',
        title: 'Enables the legacy PHP-based CSV report for bill payers',
        description:
            'While testing the new BillPayer report, it might be useful to have the old one available as well for testing. This enables the legacy report.',
        crew: 'pay',
    },
    FAMLY_HOME_NEW_LAYOUT: {
        flag: 'FAMLY_HOME_NEW_LAYOUT',
        title: 'Enable new Famly Home layout (prototype)',
        description: 'The new layout will remove the sidebar and move all widgets into the primary widget area',
        crew: 'aim',
    },
    NEW_PROFILE_MENU_LOCATION: {
        flag: 'NEW_PROFILE_MENU_LOCATION',
        title: 'Enable new profile menu location',
        description:
            'Toggle this on to use the new location in the sidebar for the profile menu. Also hides the profile menu in the topbar.',
        crew: 'aim',
    },
    INTENDED_AUDIENCE_FOR_ACTIVITIES_AND_RESOURCES: {
        flag: 'INTENDED_AUDIENCE_FOR_ACTIVITIES_AND_RESOURCES',
        title: 'Enable intended audience for activities and resources',
        description: 'Toggle this on to enable intended audience for activities and resources.',
        crew: 'aim',
    },
    DISABLE_CHILD_PROFILE_INSERT_PLAN_DIVIDERS: {
        flag: 'DISABLE_CHILD_PROFILE_INSERT_PLAN_DIVIDERS',
        title: 'Disable insert plan dividers in Child Profile',
        description:
            'Disables the dividers in the plan overview in the Child Profile for inserting plans between existing plans.',
        crew: 'aim',
    },
    NEW_FUNDING_LOOP: {
        flag: 'NEW_FUNDING_LOOP',
        title: 'Enable new funding loop tabs',
        description: 'Toggle this on to enable the new funding loop tabs in the public funding report.',
        crew: 'inv',
    },
    REDESIGN_CHART_OF_ACCOUNTS: {
        flag: 'REDESIGN_CHART_OF_ACCOUNTS',
        title: 'Enable redesign of chart of accounts',
        description: 'Toggle this on to enable the redesign of chart of accounts.',
        crew: 'inv',
    },
    PRICING_STRUCTURE: {
        flag: 'PRICING_STRUCTURE',
        title: 'Enable modern pricing structure page',
        description:
            'Toggle this on to render the modern non-DACH pricing structure settings page instead of the legacy "Items & Pricing" page.',
        crew: 'inv',
    },
    MANAGE_EXTERNAL_IDS: {
        flag: 'MANAGE_EXTERNAL_IDS',
        title: 'Show Manage External IDs component',
        description: 'Toggle this on to show the Manage External IDs component in integration settings.',
        crew: 'aim',
    },
    DEVICE_ENROLLMENT: {
        flag: 'DEVICE_ENROLLMENT',
        title: 'Device Enrollment',
        description: 'Toggle this on to enable device enrollment features.',
        crew: 'aim',
    },
    NAVIGATION_DESIGN_V3: {
        flag: 'NAVIGATION_DESIGN_V3',
        title: 'Use new V3 navigation design',
        description: 'Use new light V3 navigation design.',
        crew: 'gro',
    },
    ASSET_UPLOAD_PLAYGROUND: {
        flag: 'ASSET_UPLOAD_PLAYGROUND',
        title: 'Show Asset Upload Playground',
        description: 'Toggle this on to show the asset upload playground in Settings.',
        crew: 'go',
    },
    SIDEKICK_ASSISTANT_SITE_SETTINGS: {
        flag: 'SIDEKICK_ASSISTANT_SITE_SETTINGS',
        title: 'Show Sidekick Assistant site settings page',
        description:
            'Toggle this on to show the Sidekick Assistant site settings page (Knowledge) under Feature Settings, and the Site settings entry in the Sidekick drawer.',
        crew: 'ai',
    },
    SIDEKICK_ASSISTANT_WHILE_IMPERSONATING: {
        flag: 'SIDEKICK_ASSISTANT_WHILE_IMPERSONATING',
        title: 'Show Sidekick Assistant while impersonating',
        description:
            'Toggle this on to show the Sidekick Assistant while impersonating a customer. Sidekick is hidden during impersonation by default so it never surfaces to customers; internal staff dogfooding Sidekick in a customer context must opt in with this flag.',
        crew: 'ai',
    },
    SIDEKICK_ASSISTANT_WORKFLOWS: {
        flag: 'SIDEKICK_ASSISTANT_WORKFLOWS',
        title: 'Show Sidekick workflow picker',
        description:
            'Toggle this on to show a dev-only dropdown in the Sidekick composer for starting a workflow. Picking one switches to the agent that runs it, starts a fresh chat, and pre-fills the composer with the workflow prompt.',
        crew: 'ai',
    },
    SIDEKICK_ASSISTANT_USER_WORKFLOWS: {
        flag: 'SIDEKICK_ASSISTANT_USER_WORKFLOWS',
        title: 'Show "My workflows" on the Sidekick home page',
        description:
            'Toggle this on to let users write their own Frankie workflows on the Frankie Automations page and run or schedule them like library workflows. Experimental: workflows are stored in this browser only.',
        crew: 'ai',
    },
    FORCE_NOTIFICATION_POLLING: {
        flag: 'FORCE_NOTIFICATION_POLLING',
        title: 'Force notification polling in e2e',
        description: 'Toggle this on to force notification count polling even in e2e mode.',
        crew: 'aim',
    },
    SHOW_FUNDING_IN_CHILD_PROFILE_WITH_DRC: {
        flag: 'SHOW_FUNDING_IN_CHILD_PROFILE_WITH_DRC',
        title: 'Show Funding section in child profile when DRC is enabled',
        description:
            'Shows the Funding section in the child profile even when DAILY_RATE_WITH_COPAY_FUNDING (DRC) is enabled on the site. By default the section is hidden on DRC sites because the DRC flow replaces it; this flag is for testers who need to see the old Funding section while DRC stays on.',
        crew: 'gro',
    },
    SUBSIDY_RECONCILIATION_REPORTS: {
        flag: 'SUBSIDY_RECONCILIATION_REPORTS',
        title: 'Show subsidy reconciliation reports',
        description:
            'Shows the new sub-tabs on the US Subsidies page (Subsidy payments, Differences, Expected subsidies) while they run on mock data. Without the flag the page shows only the live Expected subsidies report.',
        crew: 'gro',
    },
    USE_NEW_VOUCHER_MODAL: {
        flag: 'USE_NEW_VOUCHER_MODAL',
        title: 'Use the new voucher modal',
        description:
            'Opens the new react-hook-form voucher modal from the bill payer profile instead of the legacy redux-form one. Only affects sites WITHOUT the VOUCHERS feature, which is nearly all of them; a site with the feature keeps its own two-step modal either way.',
        crew: 'pay',
    },
    KINDER_CONNECT_CAPTURE: {
        flag: 'KINDER_CONNECT_CAPTURE',
        title: 'KinderConnect capture (photo, signature, GPS)',
        description:
            'Toggle this on to enable the KinderConnect TWC capture flow (photo, signature, and geolocation) at attendance entry points. Required for Texas Workforce Commission compliance.',
        crew: 'stat',
    },
    SHOW_UK_ENTITLEMENTS_OPT_IN: {
        flag: 'SHOW_UK_ENTITLEMENTS_OPT_IN',
        title: 'Show entitlement-based funding opt-in',
        description:
            'Shows the "Entitlement-based funding" opt-in box in the Playground while the feature is in testing. Remove this flag once the feature ships.',
        crew: 'inv',
    },
    IMPORT_REVIEW_BULK_EDIT: {
        flag: 'IMPORT_REVIEW_BULK_EDIT',
        title: 'Enable bulk edit on the import review step',
        description:
            'Moves "include this row in the import" from the leading checkbox to a trailing Import? toggle across every spreadsheet import, and frees the checkbox to pick rows for a bulk edit: Edit room or Edit site and room in child import, Edit site in Kivan. Remove this flag once the change ships.',
        crew: 'nux',
    },
    NEW_STAFF_ABSENCE_MODAL: {
        flag: 'NEW_STAFF_ABSENCE_MODAL',
        title: 'New staff absence modal',
        description: 'Toggle this to enable the new staff absence modal.',
        crew: 'stat',
    },
    SAFEGUARDING_STRUCTURED_DATA: {
        flag: 'SAFEGUARDING_STRUCTURED_DATA',
        title: 'Safeguarding structured data',
        description:
            'Shows the structured allergy, dietary and weaning settings. Stands in for the backend entitlement until it exists; swap this flag for the real one before release.',
        crew: 'aim',
    },
} as const satisfies Record<string, FeatureFlag>;

export const ToolingLocalStorageFlags = {
    STICKY_DEBUGGING: {
        flag: 'STICKY_DEBUGGING',
        title: 'Always show the "Debugging" menu',
        description: 'Toggle this on to always show the debugging menu.',
    },
    ENABLE_INTERCOM_TEST_ENVIRONMENT: {
        flag: 'ENABLE_INTERCOM_TEST_ENVIRONMENT',
        title: 'Connect to Intercom test environment',
        description: 'Connect to Intercom test environment for debugging/dev purposes.',
    },
    SHOW_AUTO_LOGOUT_STATS: {
        flag: 'SHOW_AUTO_LOGOUT_STATS',
        title: 'Auto logout V2 status',
        description:
            'Toggle this to get an overlay that shows all details for auto logout. Auto logout feature and V2 needs to be enabled.',
    },
    TRACKING_VISUALIZATION: {
        flag: 'TRACKING_VISUALIZATION',
        title: 'Enable tracking visualisation of Intercom and Mixpanel events',
        description:
            'Enables the unified tracking visualisation tool for both Mixpanel and Intercom events. You can see tracking events for elements, control which events are shown/sent, and view a searchable live feed of events. Make sure the feature "ENABLE_ANALYTICS" is enabled as well.',
    },
    FORM_BUILDER_DEBUG_INFO: {
        flag: 'FORM_BUILDER_DEBUG_INFO',
        title: 'Adds debug info to the form builder state (for e2e)',
        description: 'Toggle this to add debug info to the form builder state (for e2e)',
    },
    TRACE_ALL_TRANSACTIONS: {
        flag: 'TRACE_ALL_TRANSACTIONS',
        title: '[Sentry] Trace all transactions',
        description: 'Turn this on to have all transactions traced in Sentry.',
    },
    SENTRY_ENABLE_REPLAY: {
        flag: 'ENABLE_REPLAY',
        title: '[Sentry] Enable Replay',
        description: 'Turn this on to enable Sentry replay. Has no effect in the native apps.',
    },
    SENTRY_DIAGNOSTIC_MODE: {
        flag: 'SENTRY_DIAGNOSTIC_MODE',
        title: '[Sentry] Diagnostic mode',
        description:
            "Turn this on to enable Sentry diagnostic mode. This will bypass the filtering we've setup in sentry-setup.ts.",
    },
    SHOW_STAT_FORM_DEBUG: {
        flag: 'SHOW_STAT_FORM_DEBUG',
        title: 'Enable stat form debug information',
    },
    ENABLE_FORM_BUILDER_DEBUG: {
        flag: 'ENABLE_FORM_BUILDER_DEBUG',
        title: 'Enable form builder debug info',
        description: 'Toggle this on to access the form builder debug info',
    },
    ENABLE_IMAGICK_IMAGE_PROCESSING: {
        flag: 'ENABLE_IMAGICK_IMAGE_PROCESSING',
        title: 'Enable Imagick image processing',
        description: 'Toggle this on to use Imagick for image processing instead of GD (in core).',
    },
    APOLLO_DEV_ERROR_MESSAGES: {
        flag: 'APOLLO_DEV_ERROR_MESSAGES',
        title: '[Apollo] Load dev error messages',
        description:
            'Toggle this on to load the verbose Apollo Client dev error messages outside of e2e (helpful when debugging minified Apollo errors locally or in production).',
    },
    FORM_FILL_DEBUGGING: {
        flag: 'FORM_FILL_DEBUGGING',
        title: 'Form fill button',
        description:
            'Shows a floating "Fill form" button that fills all visible, empty form fields with dummy data. Useful for quickly getting past mandatory fields when testing.',
    },
} as const satisfies Record<string, ToolingFlag>;

export const DebugLocalStorageFlags = {
    ...DebugFeatureLocalStorageFlags,
    ...ToolingLocalStorageFlags,
};

/**
 * I grew tired of having to import DebugLocalStorageFlags to check if a flag is enabled
 * so I added an overload that allows you to simply look up by the union of keys in DebugLocalStorageFlags
 */
type CheckIfFlagIsToggled = {
    (flag: string): boolean;
    (flag: keyof typeof DebugLocalStorageFlags): boolean;
};
export const checkIfFlagIsToggled: CheckIfFlagIsToggled = flag => {
    const lookup = DebugLocalStorageFlags[flag as keyof typeof DebugLocalStorageFlags];
    if (hasValue(lookup)) {
        return Boolean(getFromLocalStorage(lookup.flag));
    }
    return Boolean(getFromLocalStorage(flag));
};
